import { useEffect, useRef } from 'react'

// One Escape key, many open layers. A dialog that holds a dropdown that holds
// a date picker used to give each of them its own document keydown listener,
// and every one fired on the same key press: Escape in the supplier picker
// closed the whole "New bill" dialog and threw away the half-entered bill.
//
// Every dismissible surface (dialog, popover, menu) now pushes a layer while it
// is open. One document listener hands the key to the most recently opened
// layer only, so Escape peels the stack one level at a time: picker first, then
// the dialog on the next press.
//
// A component that handles Escape itself in a React onKeyDown (an autocomplete
// list, a grid) and calls stopPropagation or preventDefault keeps the key: the
// listener sits on the document in the bubble phase and skips handled events.

export interface EscapeEventLike {
  key: string
  defaultPrevented: boolean
  preventDefault: () => void
}

export interface EscapeStack {
  push: (onEscape: () => void) => () => void
  handle: (event: EscapeEventLike) => boolean
  size: () => number
}

export function createEscapeStack(): EscapeStack {
  const layers: Array<{ onEscape: () => void }> = []
  return {
    push(onEscape) {
      const layer = { onEscape }
      layers.push(layer)
      return () => {
        const at = layers.lastIndexOf(layer)
        if (at >= 0) layers.splice(at, 1)
      }
    },
    handle(event) {
      if (event.key !== 'Escape' || event.defaultPrevented) return false
      const top = layers[layers.length - 1]
      if (!top) return false
      event.preventDefault()
      top.onEscape()
      return true
    },
    size: () => layers.length,
  }
}

const stack = createEscapeStack()
let listening = false

function ensureListener() {
  if (listening || typeof document === 'undefined') return
  document.addEventListener('keydown', (event) => {
    stack.handle(event)
  })
  listening = true
}

/** Register a dismissible layer for as long as it is open. */
export function pushEscapeLayer(onEscape: () => void): () => void {
  ensureListener()
  return stack.push(onEscape)
}

/**
 * Keep `onEscape` on the escape stack while `active` is true. The latest
 * callback is always used, so callers need not memoise it.
 */
export function useEscapeLayer(active: boolean, onEscape: () => void) {
  const handler = useRef(onEscape)
  handler.current = onEscape
  useEffect(() => {
    if (!active) return
    return pushEscapeLayer(() => handler.current())
  }, [active])
}
